import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    ActionViewDefinition,
    ActionViewDetailSnapshot,
    ActionViewDisplay,
    ActionViewExternalDrilldown,
    ActionViewOverviewSnapshot,
    ActionViewRouteContext,
    ActionViewSearchRequest,
    ActionViewSessionState,
    ActionViewUiColumn,
} from './actionView.models';

@Injectable({ providedIn: 'root' })
export class ActionViewStateService {
    private readonly stateSubject: BehaviorSubject<ActionViewSessionState | null> = new BehaviorSubject(null);

    get state$(): Observable<ActionViewSessionState | null> {
        return this.stateSubject.asObservable();
    }

    get snapshot(): ActionViewSessionState | null {
        return this.stateSubject.value;
    }

    startSession(
        route: ActionViewRouteContext,
        definition: ActionViewDefinition,
        criteria: ActionViewSearchRequest,
        display: ActionViewDisplay,
        filterColumns: ActionViewUiColumn[] = [],
    ): void {
        this.stateSubject.next({
            route: structuredClone(route),
            definition: structuredClone(definition),
            display,
            masterCriteria: structuredClone(criteria),
            filterColumns: structuredClone(filterColumns),
            drilldownCriteria: null,
            overview: null,
            isOverviewTimedOut: false,
            detail: null,
            externalDrilldown: null,
            showOrs: criteria.showOrs,
            drilldownMode: false,
        });
    }

    updateFilterColumns(filterColumns: ActionViewUiColumn[]): void {
        this.update({ filterColumns: structuredClone(filterColumns) });
    }

    setDisplay(display: ActionViewDisplay): void {
        this.update({ display });
    }

    updateMasterCriteria(criteria: ActionViewSearchRequest, showOrs?: boolean): void {
        this.update({
            masterCriteria: structuredClone(criteria),
            ...(showOrs === undefined ? {} : { showOrs }),
        });
    }

    storeOverview(overview: ActionViewOverviewSnapshot): void {
        this.update({
            overview: structuredClone(overview),
            isOverviewTimedOut: false,
            display: 'overview',
            drilldownCriteria: null,
            drilldownMode: false,
        });
    }

    startOverviewSearch(): void {
        this.update({ overview: null, isOverviewTimedOut: false });
    }

    markOverviewTimedOut(): void {
        this.update({ overview: null, isOverviewTimedOut: true });
    }

    beginDrilldown(criteria: ActionViewSearchRequest): void {
        this.update({
            drilldownCriteria: structuredClone(criteria),
            display: 'detailed',
            drilldownMode: true,
            detail: null,
        });
    }

    storeDetail(detail: ActionViewDetailSnapshot): void {
        this.update({
            detail: structuredClone(detail),
            display: 'detailed',
        });
    }

    restoreOverview(): ActionViewOverviewSnapshot | null {
        const overview = this.snapshot?.overview ?? null;
        if (overview) {
            this.update({
                display: 'overview',
                drilldownCriteria: null,
                drilldownMode: false,
            });
        }
        return overview ? structuredClone(overview) : null;
    }

    /**
     * Returns the session to its criteria page and discards cached results, for pages whose processing changes
     * which tasks match (e.g. transmitting payment packages), so the user can adjust the criteria and run again.
     */
    prepareReturnToFilter(): void {
        if (!this.snapshot) {
            return;
        }
        this.update({
            display: 'filter',
            drilldownCriteria: null,
            drilldownMode: false,
            overview: null,
            isOverviewTimedOut: false,
            detail: null,
        });
    }

    prepareExternalNavigation(route?: ActionViewRouteContext): void {
        if (route) {
            this.update({ route: structuredClone(route) });
        }
    }

    setExternalDrilldown(externalDrilldown: ActionViewExternalDrilldown): void {
        this.update({ externalDrilldown: structuredClone(externalDrilldown) });
    }

    clearExternalDrilldown(): void {
        this.update({ externalDrilldown: null });
    }

    clear(): void {
        this.stateSubject.next(null);
    }

    getReturnRoute(legacyParams: ActionViewRouteContext['params']): ActionViewRouteContext {
        const route = this.snapshot?.route;
        if (route?.state === 'actionview') {
            return structuredClone(route);
        }
        return {
            state: 'actionview',
            params: structuredClone(legacyParams),
        };
    }

    private update(changes: Partial<ActionViewSessionState>): void {
        const current = this.snapshot;
        if (!current) {
            throw new Error('Action View session has not been initialized.');
        }
        this.stateSubject.next({ ...current, ...changes });
    }
}
